import {
  appAuthAtom,
  AppEvents,
  AuthState,
  clientSubscriptionsAtom,
  ConnectionFeedback,
  ConnectionState,
  debugObjectsAtom,
  eStreamConnectionStateAtom,
  Keys,
  NbLogger,
  onAppEvent,
  realtimeMarketsAtom,
  SSESource,
  userStreamConnectionStateAtom,
  UserStreamMessage,
} from '@eih-netbroker/common';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { AxiosInstanceContext } from '../../../context/AxiosInstanceContext';
import { useLocale } from '../../../context/NbLocalizationContext';
import { CloseSnackbarFn, useSnackbar } from '../../../context/SnackbarContext';
import { primaryInstanceAtom } from '../../state/primaryInstanceAtom';
import { SSE } from './SSE';
import { useUserStreamMessageHandlerWeb } from './useUserStreamMessageHandlerWeb';
import SSEConnectionStateUpdateEvent = AppEvents.SSEConnectionStateUpdateEvent;
import UserStreamMessageEvent = AppEvents.UserStreamMessageEvent;

const getFormattedMarket = (market: string) => {
  if (market.length > 0) {
    return ` ${market}`;
  }
  return '';
};

type UseSseCommunicatorProps = {
  offlineMode: boolean;
  logger: NbLogger;
};

/**
 * DO NOT USE THIS HOOK IN THE COMPONENTS
 * ONLY AVAILABLE THROUGH SSEConnectionContext
 */
export const useSseCommunicator = ({ logger }: UseSseCommunicatorProps) => {
  const authState = useAtomValue(appAuthAtom);
  const feedback = useRef<ConnectionFeedback | null>(null);
  const connectionErrorSnackbar = useRef<CloseSnackbarFn | null>(null);
  const connectionSuccessSnackbar = useRef<CloseSnackbarFn | null>(null);
  const clientSubscriptions = useAtomValue(clientSubscriptionsAtom);

  const { showError, showSuccess } = useSnackbar();
  const { translation, translateWithParams } = useLocale();
  const { handleUserStreamMessage } = useUserStreamMessageHandlerWeb();

  const [eStreamConnectionState, setEStreamConnectionState] = useAtom(eStreamConnectionStateAtom);
  const [, setUserStreamConnectionState] = useAtom(userStreamConnectionStateAtom);
  const [, setRealtimeMarkets] = useAtom(realtimeMarketsAtom);
  const [, setDebugObjects] = useAtom(debugObjectsAtom);
  const isPrimaryInstance = useAtomValue(primaryInstanceAtom);

  const axiosInstanceContext = useContext(AxiosInstanceContext);
  if (!axiosInstanceContext) {
    throw new Error('AxiosInstanceContext is not initialized');
  }

  /**
   * Hides the connection error snackbar if there is any
   * @private
   */
  const hideErrorSnackbar = useCallback(() => {
    if (connectionErrorSnackbar.current) {
      connectionErrorSnackbar.current();
      connectionErrorSnackbar.current = null;
    }
  }, []);

  /**
   * Hides the connection success snackbar if there is any
   * @private
   */
  const hideSuccessSnackbar = useCallback(() => {
    if (connectionSuccessSnackbar.current) {
      connectionSuccessSnackbar.current();
      connectionSuccessSnackbar.current = null;
    }
  }, []);

  /**
   * Shows an error message, that the EStream connection init failed
   */
  const showConnectionInitFailed = useCallback(
    (market = '') => {
      hideSuccessSnackbar();
      connectionErrorSnackbar.current = showError(
        translateWithParams(translation.app.estream.connectionInitFailed, {
          market: getFormattedMarket(market),
        }),
        { persist: true },
      );
    },
    [showError, translateWithParams, translation, hideSuccessSnackbar],
  );

  /**
   * Shows an error message, that the EStream has been disconnected
   */
  const showConnectionFailed = useCallback(
    (market = '') => {
      hideErrorSnackbar();
      connectionErrorSnackbar.current = showError(
        translateWithParams(translation.app.estream.connectionDisconnected, {
          market: getFormattedMarket(market),
        }),
        { persist: true },
      );
    },
    [showError, translateWithParams, translation, hideErrorSnackbar],
  );

  /**
   * Shows a success message, that the EStream has been reconnected
   */
  const showConnectionReconnected = useCallback(
    (market = '') => {
      hideErrorSnackbar();
      hideSuccessSnackbar();
      connectionSuccessSnackbar.current = showSuccess(
        translateWithParams(translation.app.estream.connectionReconnected, {
          market: getFormattedMarket(market),
        }),
      );
    },
    [showSuccess, translateWithParams, translation, hideErrorSnackbar, hideSuccessSnackbar],
  );

  const onEStreamConnectionStateChange = useCallback(
    (connectionState: ConnectionState) => {
      setDebugObjects({ EStreamConnectionState: connectionState });

      logger.info(`onEStreamConnectionStateChange: connectionState=${connectionState}, from=${eStreamConnectionState}`);

      if (connectionState === ConnectionState.Disconnected || connectionState === ConnectionState.FailedToConnected) {
        if (
          authState.isAuthenticated &&
          authState.authStatus === AuthState.Authenticated &&
          feedback.current !== ConnectionFeedback.ConnectionFailed &&
          SSE.sseCommunicator.eStreamConnection.hasToken()
        ) {
          if (eStreamConnectionState === ConnectionState.Connected) {
            showConnectionFailed();
          } else {
            showConnectionInitFailed();
          }
        }
        feedback.current = ConnectionFeedback.ConnectionFailed;
      } else if (connectionState === ConnectionState.Connected) {
        if (
          authState.isAuthenticated &&
          feedback.current !== null &&
          feedback.current !== ConnectionFeedback.Connected
        ) {
          showConnectionReconnected();
        }
        feedback.current = ConnectionFeedback.Connected;
      }

      setEStreamConnectionState(connectionState);
    },
    [
      authState,
      eStreamConnectionState,
      logger,
      setDebugObjects,
      setEStreamConnectionState,
      showConnectionFailed,
      showConnectionInitFailed,
      showConnectionReconnected,
    ],
  );

  const onUserStreamMessageReceived = useCallback(
    (message: UserStreamMessage) => {
      handleUserStreamMessage(message);
    },
    [handleUserStreamMessage],
  );

  useEffect(() => {
    const listeners = [
      onAppEvent(SSEConnectionStateUpdateEvent.type, (event: SSEConnectionStateUpdateEvent) => {
        if (event.data.source === SSESource.EStream) {
          onEStreamConnectionStateChange(event.data.connectionState);
        } else if (event.data.source === SSESource.UserStream) {
          setUserStreamConnectionState(event.data.connectionState);
        }
      }),
      onAppEvent(UserStreamMessageEvent.type, (event: UserStreamMessageEvent) => {
        // ignore user stream messages if we are not the primary instance and not authenticated
        // this is needed, because the primary instance will receive the portfolio and we don't want
        // other instances load portfolio until complete authentication done on the primary instance
        // that is mainly because we don't have estream session id from the primary instance until it
        // completes everything
        // inventory will be loaded another way, through broadcasted messages
        if (!isPrimaryInstance && authState.authStatus !== AuthState.Authenticated) {
          return;
        }

        onUserStreamMessageReceived(event.data);
      }),
    ];
    return () => {
      listeners.forEach(listener => listener());
    };
  }, [
    onEStreamConnectionStateChange,
    onUserStreamMessageReceived,
    authState,
    isPrimaryInstance,
    setUserStreamConnectionState,
  ]);

  useEffect(() => {
    const realtimeMarkets = clientSubscriptions
      .filter(_ => _.realtime)
      .map(
        subscription =>
          Keys.Markets.dataProviderMicList[
            subscription.marketDataProvider as keyof typeof Keys.Markets.dataProviderMicList
          ] || [],
      )
      .flat();

    setRealtimeMarkets(realtimeMarkets);
  }, [clientSubscriptions, setRealtimeMarkets]);

  // cleanup on logout
  useEffect(() => {
    if (authState.authStatus === AuthState.NotAuthenticated) {
      if (feedback.current) {
        feedback.current = null;
      }
      hideErrorSnackbar();
      hideSuccessSnackbar();
    }
  }, [authState, hideErrorSnackbar, hideSuccessSnackbar]);
};
